/**
 * UploadProgressDock.jsx — Bottom-right progress bar for background
 * uploads. Persists across route changes so switching tabs mid-upload
 * doesn't hide progress. Auto-dismisses successful uploads after 4 s.
 *
 * Accessibility: uses an aria-live="polite" region so screen readers
 * announce upload completion + failure without stealing focus.
 */
import { useEffect, useMemo, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { CheckCircle2, XCircle, Loader2, X, Ban } from 'lucide-react';
import { useUploadStore } from '../../stores/uploadStore';

export const UploadProgressDock = () => {
  const uploads = useUploadStore((s) => s.uploads);
  const dismiss = useUploadStore((s) => s.dismiss);
  const cancel = useUploadStore((s) => s.cancel);
  const list = Object.values(uploads).sort((a, b) => b.startedAt - a.startedAt);
  const announcedRef = useRef(new Set());

  // Build the a11y announcement string for uploads that JUST finished
  // (done / failed / cancelled). We track which ids have been announced
  // so screen readers don't repeat themselves on every re-render.
  const announcement = useMemo(() => {
    const msgs = [];
    for (const u of list) {
      const key = `${u.id}:${u.phase}`;
      if (['done', 'failed', 'cancelled'].includes(u.phase) && !announcedRef.current.has(key)) {
        announcedRef.current.add(key);
        if (u.phase === 'done') msgs.push(`${u.filename} finished uploading to ${u.kbName || 'knowledge base'}.`);
        else if (u.phase === 'failed') msgs.push(`${u.filename} upload failed: ${u.error || 'unknown error'}.`);
        else msgs.push(`${u.filename} upload cancelled.`);
      }
    }
    return msgs.join(' ');
  }, [list]);

  useEffect(() => {
    const done = list.filter((u) => u.phase === 'done');
    if (done.length) {
      const t = setTimeout(() => done.forEach((u) => dismiss(u.id)), 4000);
      return () => clearTimeout(t);
    }
  }, [list, dismiss]);

  const showDock = list.length > 0;

  return (
    <>
      {/* Screen-reader-only live region — always mounted so announcements
          land reliably, even before the visible dock renders. */}
      <div aria-live="polite" aria-atomic="true"
        className="sr-only" data-testid="upload-live-region">
        {announcement}
      </div>

      {showDock && (
        <div className="fixed bottom-4 right-4 z-40 space-y-2 max-w-sm" data-testid="upload-dock">
          <AnimatePresence>
            {list.map((u) => {
              const inFlight = u.phase === 'uploading' || u.phase === 'extracting';
              return (
                <motion.div
                  key={u.id}
                  layout
                  initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, x: 20 }}
                  className="rounded-lg p-3 shadow-xl"
                  style={{
                    background: 'var(--layer-0)',
                    border: `1px solid ${u.phase === 'failed' || u.phase === 'cancelled' ? 'rgba(255,80,80,0.5)' : 'var(--hairline)'}`,
                    minWidth: 260,
                  }}
                  data-testid={`upload-item-${u.id}`}>
                  <div className="flex items-start gap-2">
                    <div className="mt-0.5 flex-shrink-0">
                      {u.phase === 'done' ? <CheckCircle2 size={14} className="text-emerald-500" /> :
                       u.phase === 'failed' ? <XCircle size={14} className="text-red-500" /> :
                       u.phase === 'cancelled' ? <Ban size={14} className="text-red-500" /> :
                       <Loader2 size={14} className="animate-spin" style={{ color: '#F26B1F' }} />}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-[12px] font-semibold text-nexus-text-primary truncate">{u.filename}</div>
                      <div className="text-[10.5px] text-nexus-text-tertiary">
                        {u.phase === 'uploading' && `Uploading to ${u.kbName || 'KB'} · ${u.progress}%`}
                        {u.phase === 'extracting' && 'Extracting rooms + dimensions…'}
                        {u.phase === 'done' && `Analysed · ${u.kbName || 'KB'}`}
                        {u.phase === 'failed' && (u.error || 'Failed')}
                        {u.phase === 'cancelled' && 'Cancelled'}
                      </div>
                      {inFlight && (
                        <div className="mt-1.5 h-1 rounded-full overflow-hidden" style={{ background: 'var(--layer-2)' }}>
                          <motion.div
                            layout
                            className="h-full"
                            style={{
                              background: '#F26B1F',
                              width: u.phase === 'extracting' ? '80%' : `${u.progress}%`,
                            }}
                            transition={{ duration: 0.3 }}
                          />
                        </div>
                      )}
                    </div>
                    <div className="flex flex-col gap-1 flex-shrink-0">
                      {inFlight && u.cancellable !== false && (
                        <button onClick={() => cancel(u.id)}
                          className="p-0.5 rounded hover:bg-nexus-bg-tertiary text-nexus-text-tertiary hover:text-red-500"
                          title="Cancel upload"
                          data-testid={`upload-cancel-${u.id}`}>
                          <Ban size={11} />
                        </button>
                      )}
                      <button onClick={() => dismiss(u.id)}
                        className="p-0.5 rounded hover:bg-nexus-bg-tertiary text-nexus-text-tertiary"
                        title="Dismiss"
                        data-testid={`upload-dismiss-${u.id}`}>
                        <X size={11} />
                      </button>
                    </div>
                  </div>
                </motion.div>
              );
            })}
          </AnimatePresence>
        </div>
      )}
    </>
  );
};
